import { useEffect, useState } from 'react'
import { type CETAClassroomAndFocusStudents } from '../models/CETAClassroomAndFocusStudents'
import {
  type UpdateCetaClassroomAndFocusStudentsFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityThree/CetaClassroomsandFocusStudentsModal'
import { GetCetaClassroomsAndFocusStudents, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { PriorityThreeActivityName } from '../models/PriorityThreeActivityName'

interface UseCetaClassroomsAndFocusStudentsType {
  cetaClassroomsAndFocusStudents: CETAClassroomAndFocusStudents[]
  isLoading: boolean
  reloadCetaClassroomsAndFocusStudents: () => Promise<void>
}

export const useCetaClassroomsAndFocusStudents = (priorityId: number, implementationPhase?: boolean): UseCetaClassroomsAndFocusStudentsType => {
  const [cetaClassroomsAndFocusStudents, setCetaClassroomsAndFocusStudents] = useState<CETAClassroomAndFocusStudents[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadCetaClassroomsAndFocusStudents = async (): Promise<void> => {
    setIsLoading(true)

    const cetaClassroomsAndFocusStudents = await GetCetaClassroomsAndFocusStudents(priorityId, implementationPhase !== true)
    setCetaClassroomsAndFocusStudents(cetaClassroomsAndFocusStudents)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadCetaClassroomsAndFocusStudents()
  }, [])

  return { cetaClassroomsAndFocusStudents, isLoading, reloadCetaClassroomsAndFocusStudents: loadCetaClassroomsAndFocusStudents }
}

interface UseCetaClassroomsAndFocusStudentsModalType {
  handleAddCetaClassroomAndFocusStudents: () => void
  handleEditCetaClassroomAndFocusStudents: (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents) => Promise<void>
  handleCloseModal: () => Promise<void>
  initialValues?: UpdateCetaClassroomAndFocusStudentsFormValues
  handleTrackClassroomAndFocusStudent: (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents) => Promise<void>
  handleCloseTrackingModal: () => Promise<void>
  classroomAndFocusStudentToTrack: CETAClassroomAndFocusStudents | null
  modalState: 'add' | 'edit' | 'track' | 'none'
}

export const useCetaClassroomsAndFocusStudentsModal = (): UseCetaClassroomsAndFocusStudentsModalType => {
  const [modalState, setModalState] = useState<'add' | 'edit' | 'track' | 'none'>('none')
  // TODO: Make this be based off CETAClassroomAndFocusStudents type instead of form values, then have the component map to form values. This make use of a currentValue for all update and track
  const [initialValues, setInitialValues] = useState<UpdateCetaClassroomAndFocusStudentsFormValues>()
  const [classroomAndFocusStudentToTrack, setClassroomAndFocusStudentToTrack] = useState<CETAClassroomAndFocusStudents | null>(null)

  const handleAddCetaClassroomAndFocusStudents = (): void => {
    setInitialValues(undefined)
    setModalState('add')
  }

  const handleEditCetaClassroomAndFocusStudents = async (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents): Promise<void> => {
    setInitialValues({
      ...cetaClassroomAndFocusStudents,
      organizationId: cetaClassroomAndFocusStudents.organization.id,
      buildingId: cetaClassroomAndFocusStudents.building.id,
      budgetRequest: cetaClassroomAndFocusStudents.adjustedBudgetRequest != null
        ? String(cetaClassroomAndFocusStudents.adjustedBudgetRequest).replace('$', '')
        : String(cetaClassroomAndFocusStudents.budgetRequest).replace('$', ''),
      isUpdate: cetaClassroomAndFocusStudents.isUpdate ?? false,
      classrooms: cetaClassroomAndFocusStudents.classrooms ?? [],
      priorityId: cetaClassroomAndFocusStudents.priorityId
    })
    setModalState('edit')
    await LockActivity(
      cetaClassroomAndFocusStudents.id,
      PriorityThreeActivityName.CetaClassroomsAndFocusStudents,
      cetaClassroomAndFocusStudents.isUpdate
    )
  }

  const handleCloseModal = async (): Promise<void> => {
    setModalState('none')
    if (initialValues != null) {
      await UnlockActivity(
        initialValues.id,
        PriorityThreeActivityName.CetaClassroomsAndFocusStudents,
        initialValues.isUpdate
      )
    }
  }

  const handleTrackClassroomAndFocusStudent = async (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents): Promise<void> => {
    setClassroomAndFocusStudentToTrack(cetaClassroomAndFocusStudents)
    setModalState('track')

    await LockActivity(
      cetaClassroomAndFocusStudents.id,
      PriorityThreeActivityName.CetaClassroomsAndFocusStudents,
      cetaClassroomAndFocusStudents.isUpdate
    )
  }

  const handleCloseTrackingModal = async (): Promise<void> => {
    if (classroomAndFocusStudentToTrack != null) {
      await UnlockActivity(
        classroomAndFocusStudentToTrack.id,
        PriorityThreeActivityName.CetaClassroomsAndFocusStudents,
        classroomAndFocusStudentToTrack?.isUpdate
      )
    }

    setClassroomAndFocusStudentToTrack(null)
    setModalState('none')
  }

  return {
    modalState,
    handleAddCetaClassroomAndFocusStudents,
    handleEditCetaClassroomAndFocusStudents,
    handleCloseModal,
    initialValues,
    handleTrackClassroomAndFocusStudent,
    handleCloseTrackingModal,
    classroomAndFocusStudentToTrack
  }
}
